import { useState, useEffect } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Loader2, Plus, Users, Settings as SettingsIcon, Briefcase, Trash2, Check } from "lucide-react";
import { API } from "@/config";
import { useAuth } from "@/components/AuthProvider";
import { toast } from "sonner";

const ROLES = [
  { value: "owner", label: "Owner", desc: "Full control, billing, member management" },
  { value: "editor", label: "Editor", desc: "Create + edit content, brand voice, branding" },
  { value: "viewer", label: "Viewer", desc: "Read-only access" },
];

export default function WorkspacesPage() {
  const { user, updateUser } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [selected, setSelected] = useState(null); // workspace_id of detail panel
  const navigate = useNavigate();

  const isEnterprise = user?.plan === "enterprise";

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/workspaces`);
      setWorkspaces(r.data.workspaces || []);
      setActiveId(r.data.active_workspace_id || null);
    } catch (e) { /* swallow */ }
  };

  useEffect(() => { refresh(); }, []);

  const create = async () => {
    if (name.trim().length < 2) { toast.error("Workspace name too short"); return; }
    setCreating(true);
    try {
      const r = await axios.post(`${API}/workspaces`, { name: name.trim(), slug: slug.trim() || undefined });
      toast.success(`Workspace "${r.data.name}" created`);
      setName(""); setSlug("");
      await refresh();
      setSelected(r.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create workspace");
    }
    setCreating(false);
  };

  const switchTo = async (id) => {
    try {
      await axios.post(`${API}/workspaces/switch/${id}`);
      setActiveId(id);
      toast.success("Active workspace switched");
      await refresh();
    } catch (e) {
      toast.error("Could not switch workspace");
    }
  };

  if (!isEnterprise) {
    return (
      <div className="space-y-6" data-testid="workspaces-page">
        <h1 className="text-3xl font-bold mb-2 flex items-center gap-2"><Briefcase className="w-8 h-8 text-violet-500" /> Agency Workspaces</h1>
        <Card className="glass border-amber-500/30">
          <CardContent className="p-8">
            <Badge className="mb-3 bg-amber-600">Enterprise required</Badge>
            <h2 className="text-2xl font-bold mb-3">Run multiple clients from one account</h2>
            <p className="text-muted-foreground mb-6 max-w-2xl">
              Multi-tenant workspaces are designed for agencies and consultants managing content for several clients at once. Each workspace has its own brand identity, team members, content library, and per-client usage caps. Available on the Enterprise plan.
            </p>
            <Link to="/dashboard/settings"><Button className="bg-violet-600 hover:bg-violet-700 rounded-full" data-testid="upgrade-from-workspaces">Upgrade to Enterprise</Button></Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="workspaces-page">
      <div>
        <h1 className="text-3xl font-bold mb-2 flex items-center gap-2"><Briefcase className="w-8 h-8 text-violet-500" /> Agency Workspaces</h1>
        <p className="text-muted-foreground">Spin up a branded workspace for each client. Manage team seats, set per-client quotas, and ship content under your agency's name.</p>
      </div>

      <Card className="glass">
        <CardHeader><CardTitle>Create a new workspace</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Client / workspace name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Acme Studios" className="mt-1 bg-white/5 border-white/10" data-testid="workspaces-new-name" />
            </div>
            <div>
              <Label>URL slug (optional)</Label>
              <Input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} placeholder="acme-studios" className="mt-1 bg-white/5 border-white/10" data-testid="workspaces-new-slug" />
              <p className="text-xs text-muted-foreground mt-1">Lives at <code>/w/{slug || "your-slug"}</code> with branded login.</p>
            </div>
          </div>
          <Button onClick={create} disabled={creating} className="bg-violet-600 hover:bg-violet-700 rounded-full" data-testid="workspaces-create-btn">
            {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            Create workspace
          </Button>
        </CardContent>
      </Card>

      <div>
        <h2 className="text-xl font-semibold mb-3">Your workspaces</h2>
        {workspaces.length === 0 ? (
          <p className="text-muted-foreground text-sm">No workspaces yet. Create your first one above.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {workspaces.map((ws) => (
              <Card key={ws.id} className={`glass card-hover ${activeId === ws.id ? "border-violet-500/40" : ""}`} data-testid={`workspace-${ws.slug}`}>
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-baseline justify-between gap-2 flex-wrap">
                    <div>
                      <p className="text-lg font-bold">{ws.branding?.brand_name || ws.name}</p>
                      <p className="text-xs text-muted-foreground">/{ws.slug} · {ws.members?.length || 0} member{(ws.members?.length || 0) === 1 ? "" : "s"}</p>
                    </div>
                    {activeId === ws.id ? (
                      <Badge className="bg-teal-600"><Check className="w-3 h-3 mr-1" /> Active</Badge>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => switchTo(ws.id)} data-testid={`workspace-switch-${ws.slug}`}>Switch to</Button>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" variant="ghost" onClick={() => setSelected(selected === ws.id ? null : ws.id)} data-testid={`workspace-manage-${ws.slug}`}><SettingsIcon className="w-4 h-4 mr-1" /> Manage</Button>
                    <a href={`/w/${ws.slug}`} target="_blank" rel="noreferrer" className="text-sm text-violet-400 hover:underline self-center" data-testid={`workspace-public-${ws.slug}`}>View public login →</a>
                  </div>
                  {selected === ws.id && <WorkspaceDetail workspace={ws} onChange={refresh} />}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const WorkspaceDetail = ({ workspace, onChange }) => {
  const [ws, setWs] = useState(workspace);
  const [loading, setLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("editor");
  const [brandName, setBrandName] = useState(ws.branding?.brand_name || "");
  const [color, setColor] = useState(ws.branding?.primary_color || "#7C5CFC");
  const [contentLimit, setContentLimit] = useState(ws.quota_overrides?.content_limit ?? -1);
  const [videoLimit, setVideoLimit] = useState(ws.quota_overrides?.video_limit ?? -1);
  const [campaignLimit, setCampaignLimit] = useState(ws.quota_overrides?.campaign_limit ?? -1);

  const fetchDetail = async () => {
    try { const r = await axios.get(`${API}/workspaces/${ws.id}`); setWs(r.data); } catch (e) {}
  };
  useEffect(() => { fetchDetail(); }, []);

  const saveSettings = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/workspaces/${ws.id}`, {
        brand_name: brandName, primary_color: color,
        content_limit: parseInt(contentLimit), video_limit: parseInt(videoLimit), campaign_limit: parseInt(campaignLimit),
      });
      toast.success("Settings saved");
      await fetchDetail(); onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setLoading(false);
  };

  const invite = async () => {
    if (!inviteEmail.trim()) return;
    try {
      const r = await axios.post(`${API}/workspaces/${ws.id}/members`, { email: inviteEmail.trim(), role: inviteRole });
      if (r.data.status === "added") toast.success(`${inviteEmail} added as ${inviteRole}`);
      else toast.success(`Invite sent — link: ${window.location.origin}${r.data.invite_link}`);
      setInviteEmail("");
      await fetchDetail();
    } catch (e) { toast.error(e.response?.data?.detail || "Invite failed"); }
  };

  const removeMember = async (uid) => {
    if (!window.confirm("Remove this member from the workspace?")) return;
    try { await axios.delete(`${API}/workspaces/${ws.id}/members/${uid}`); toast.success("Removed"); await fetchDetail(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="mt-3 pt-3 border-t border-white/10 space-y-4" data-testid={`workspace-detail-${ws.slug}`}>
      {/* Branding */}
      <div>
        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Branding</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Brand name shown to client</Label>
            <Input value={brandName} onChange={(e) => setBrandName(e.target.value)} className="mt-1 bg-white/5 border-white/10" data-testid={`workspace-brand-name-${ws.slug}`} />
          </div>
          <div>
            <Label className="text-xs">Primary color</Label>
            <Input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="mt-1 bg-white/5 border-white/10 h-10" data-testid={`workspace-color-${ws.slug}`} />
          </div>
        </div>
      </div>
      {/* Per-client quotas */}
      <div>
        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Monthly caps for this client (-1 = unlimited, 0 = disabled)</p>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Content</Label>
            <Input type="number" value={contentLimit} onChange={(e) => setContentLimit(e.target.value)} className="mt-1 bg-white/5 border-white/10" data-testid={`workspace-cap-content-${ws.slug}`} />
          </div>
          <div>
            <Label className="text-xs">Videos</Label>
            <Input type="number" value={videoLimit} onChange={(e) => setVideoLimit(e.target.value)} className="mt-1 bg-white/5 border-white/10" data-testid={`workspace-cap-video-${ws.slug}`} />
          </div>
          <div>
            <Label className="text-xs">Campaigns</Label>
            <Input type="number" value={campaignLimit} onChange={(e) => setCampaignLimit(e.target.value)} className="mt-1 bg-white/5 border-white/10" data-testid={`workspace-cap-campaign-${ws.slug}`} />
          </div>
        </div>
      </div>
      <Button onClick={saveSettings} disabled={loading} size="sm" className="bg-violet-600 hover:bg-violet-700 rounded-full" data-testid={`workspace-save-${ws.slug}`}>
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Save settings
      </Button>

      {/* Members */}
      <div>
        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1"><Users className="w-3 h-3" /> Team</p>
        <div className="space-y-2 mb-3">
          {(ws.members || []).map((m) => (
            <div key={m.user_id} className="flex items-center justify-between gap-2 p-2 rounded-lg bg-white/5">
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{m.name || m.email || m.user_id.slice(0, 8)}</p>
                <p className="text-xs text-muted-foreground truncate">{m.email} · {m.role}</p>
              </div>
              {m.role !== "owner" && (
                <Button variant="ghost" size="sm" onClick={() => removeMember(m.user_id)} data-testid={`workspace-remove-${m.user_id}`}><Trash2 className="w-4 h-4" /></Button>
              )}
            </div>
          ))}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="teammate@example.com" className="flex-1 bg-white/5 border-white/10 min-w-[200px]" data-testid={`workspace-invite-email-${ws.slug}`} />
          <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} className="bg-white/5 border border-white/10 rounded-md px-3 text-sm" data-testid={`workspace-invite-role-${ws.slug}`}>
            {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <Button onClick={invite} size="sm" variant="outline" data-testid={`workspace-invite-btn-${ws.slug}`}>Invite</Button>
        </div>
      </div>
    </div>
  );
};
